import { useState, type FormEvent } from "react";
import { DialogProps } from "../../types/dialog-types";
import { getTigerResource } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import { changePassword } from "../../auth/webAuth";

/**
 * Change the signed-in user's password (web build only). Port of the C#
 * `ManageController.ChangePassword`: the current password is required, and the
 * backend rotates `SecurityStamp`, which drops the user's *other* sessions while
 * keeping this one.
 *
 * Unlike `LoginScreen` (which hand-rolls a dark acrylic variant for the
 * pre-auth overlay), this is an in-app dialog and so uses the standard acrylic
 * dialog tokens.
 */
export function ChangePasswordDialog({ open, onClose }: DialogProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Checked here too, to save a round trip on an obvious typo.
    if (newPassword !== confirm) {
      setErrors([getTigerResource("TIGER_LOGIN_PWD_MISMATCH")]);
      return;
    }
    setBusy(true);
    setErrors([]);
    const errs = await changePassword(oldPassword, newPassword, confirm);
    setBusy(false);
    if (errs.length) setErrors(errs);
    else setDone(true);
  }

  if (!open) return null;

  const title = getTigerResource("TIGER_LOGIN_CHANGE_PASSWORD_TITLE");

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={title}
      >
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {done ? (
          <>
            <div style={styles.body}>
              <span style={styles.message}>
                {getTigerResource("TIGER_LOGIN_CHANGE_PWD_OK")}
              </span>
            </div>
            <div style={styles.footer}>
              <button className="btn-acrylic-primary" onClick={onClose}>
                {getTigerResource("TIGER_RES_CLOSE")}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <div style={styles.body}>
              <PasswordField
                label={getTigerResource("TIGER_LOGIN_CURRENT_PASSWORD")}
                value={oldPassword}
                onChange={setOldPassword}
                autoFocus
              />
              <PasswordField
                label={getTigerResource("TIGER_LOGIN_NEW_PASSWORD")}
                value={newPassword}
                onChange={setNewPassword}
              />
              <PasswordField
                label={getTigerResource("TIGER_LOGIN_CONFIRM_PASSWORD")}
                value={confirm}
                onChange={setConfirm}
              />
              {errors.map((m) => (
                <div key={m} style={styles.error}>
                  {m}
                </div>
              ))}
            </div>
            <div style={styles.footer}>
              <button type="button" className="btn-acrylic-secondary" onClick={onClose}>
                {getTigerResource("TIGER_RES_CANCEL")}
              </button>
              <button type="submit" className="btn-acrylic-primary" disabled={busy}>
                {busy ? getTigerResource("TIGER_LOGIN_SAVING") : title}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        aria-label={label}
        type="password"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
      />
    </label>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 380,
    maxWidth: "92vw",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
  },
  body: {
    padding: "18px 24px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
  },
  message: { fontSize: 14, color: "#4b5260" },
  field: { display: "flex", flexDirection: "column" as const, gap: 4 },
  fieldLabel: { fontSize: 12, color: "#4b5260" },
  input: {
    padding: "7px 9px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.15)",
    fontSize: 13,
  },
  error: { fontSize: 12, color: "#b91c1c" },
} as const;
